import type { AxiosResponse } from 'axios'
import axios from 'axios'

import environment from '@/environment'
import type {
  CreateSite,
  CreateSiteContactRequest,
  DeleteSiteContactRequest,
  FinancialInformation,
  Site,
  SiteActivationOrderSettingsRequest,
  SiteContact,
  SiteMarketProgramContractRequest,
  SiteNotificationPreferencesRequest,
  UpdateSiteContactRequest,
  UpdateSiteInformation,
} from '@/features/site/types/site'

export const GET_SITES_API_ID = 'GET_SITES'
export const GET_SITE_API_ID = 'GET_SITE'

export const ERROR_NAMES = {
  VALIDATION_FAILED: 'validation-failed',
}

export async function getSites(location?: string): Promise<Site[]> {
  const response = await axios.get(`${environment.services.sitesManagerApiUrl}`, {
    params: {
      location,
    },
  })
  return response.data
}

export async function getSite(siteUuid: string, location?: string): Promise<Site> {
  const response = await axios.get(`${environment.services.sitesManagerApiUrl}/${siteUuid}`, {
    params: {
      location,
    },
  })
  return response.data
}

export async function saveSite(site: CreateSite): Promise<string> {
  const response = await axios.post<string>(`${environment.services.sitesManagerApiUrl}`, site)

  return response.data
}

export async function updateSiteInformation(
  siteUuid: string,
  params: UpdateSiteInformation,
): Promise<AxiosResponse<void, UpdateSiteInformation>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/information`)

  url.searchParams.append('location', params.location)

  return axios.put<void, AxiosResponse<void, UpdateSiteInformation>, UpdateSiteInformation>(url.toString(), params)
}

export async function updateSiteFinancialInformation(
  siteUuid: string,
  location: string,
  params: FinancialInformation,
): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/financial-information`)

  url.searchParams.append('location', location)

  return axios.put<void, AxiosResponse<void, string>, FinancialInformation>(url.toString(), params)
}

export async function updateSiteMarketProgramContracts(
  siteUuid: string,
  location: string,
  params: SiteMarketProgramContractRequest[],
): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/market-program-contracts`)

  url.searchParams.append('location', location)

  return axios.put<void, AxiosResponse<void, string>, SiteMarketProgramContractRequest[]>(url.toString(), params)
}

export async function updateSiteNotificationPreferences(
  siteUuid: string,
  location: string,
  params: SiteNotificationPreferencesRequest,
): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/notification-preferences`)

  url.searchParams.append('location', location)

  return axios.put<void, AxiosResponse<void, string>, SiteNotificationPreferencesRequest>(url.toString(), params)
}

export async function updateSiteActivationOrderSettings(
  siteUuid: string,
  location: string,
  payload: SiteActivationOrderSettingsRequest,
): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/activation-order-settings`)

  url.searchParams.append('location', location)

  return axios.put<void, AxiosResponse<void, string>, SiteActivationOrderSettingsRequest>(url.toString(), payload)
}

export async function createSiteContact({
  siteUuid,
  location,
  contact,
}: CreateSiteContactRequest): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/contacts`)

  url.searchParams.append('location', location)

  return axios.post<void, AxiosResponse<void, string>, SiteContact>(url.toString(), contact)
}

export async function updateSiteContact({
  siteUuid,
  location,
  contact,
}: UpdateSiteContactRequest): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/contacts/${contact.legacyId}`)

  url.searchParams.append('location', location)

  return axios.put<void, AxiosResponse<void, string>, SiteContact>(url.toString(), contact)
}

export async function deleteSiteContact({
  siteUuid,
  contactId,
  location,
}: DeleteSiteContactRequest): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/contacts/${contactId}`)

  url.searchParams.append('location', location)

  return axios.delete<void, AxiosResponse<void, string>, SiteContact>(url.toString())
}

export async function enableSite(siteUuid: string, location: string): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/enable`)

  url.searchParams.append('location', location)

  return axios.put<void, AxiosResponse<void, string>>(url.toString())
}

export async function disableSite(siteUuid: string, location: string): Promise<AxiosResponse<void, string>> {
  const url = new URL(`${environment.services.sitesManagerApiUrl}/${siteUuid}/disable`)

  url.searchParams.append('location', location)

  return axios.put<void, AxiosResponse<void, string>>(url.toString())
}
